'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PresetsPanel from '@/components/PresetsPanel';
import { usePresets } from '@/lib/usePresets';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2];

interface SpeedPresetSettings {
  speed: number;
  outputFormat: 'mp3' | 'wav';
}

export default function AudioSpeedChangerTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number } | null>(null);
  const [originalDuration, setOriginalDuration] = useState<number | null>(null);
  const [speed, setSpeed] = useState(1.0);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement>(null);

  const { presets, savePreset, deletePreset } = usePresets<SpeedPresetSettings>('audio-speed-changer');

  const getCurrentSettings = (): SpeedPresetSettings => ({ speed, outputFormat });

  const applyPresetSettings = (settings: SpeedPresetSettings) => {
    setSpeed(settings.speed);
    setOutputFormat(settings.outputFormat);
    setResultUrl(null);
  };

  // Update preview player playback rate when speed changes
  useEffect(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.playbackRate = speed;
    }
  }, [speed]);

  const loadFile = useCallback((file: File) => {
    const allowed = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/webm', 'audio/flac', 'audio/x-flac'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowedExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm', 'flac'];
    if (!allowed.includes(file.type) && !allowedExts.includes(ext)) {
      setError('Unsupported file type. Please upload MP3, WAV, OGG, M4A, AAC, WebM, or FLAC.');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError('File too large. Maximum supported size is 200 MB.');
      return;
    }
    setError(null);
    setResultUrl(null);
    setOriginalDuration(null);
    const url = URL.createObjectURL(file);
    setAudioFile({ file, url, size: file.size });
  }, []);

  const handleAudioLoaded = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const dur = e.currentTarget.duration;
    if (isFinite(dur)) setOriginalDuration(dur);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const handleReset = () => {
    setAudioFile(null);
    setOriginalDuration(null);
    setSpeed(1.0);
    setResultUrl(null);
    setResultSize(0);
    setError(null);
    setProgressPct(0);
    setProgressLabel('');
  };

  const handleProcess = async () => {
    if (!audioFile) return;
    setProcessing(true);
    setProgressPct(5);
    setProgressLabel('Loading ffmpeg…');
    setError(null);
    setResultUrl(null);

    try {
      // Dynamic import of ffmpeg
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      setProgressPct(15);
      setProgressLabel('Initialising ffmpeg…');

      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgressPct(Math.round(20 + progress * 70));
        setProgressLabel(`Processing… ${Math.round(progress * 100)}%`);
      });

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressPct(22);
      setProgressLabel('Reading file…');

      const inputExt = audioFile.file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
      const inputName = `input.${inputExt}`;
      const outputName = `output.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(audioFile.file));

      setProgressPct(25);
      setProgressLabel('Applying speed change…');

      // Build atempo filter chain — atempo only accepts 0.5–2.0 per filter
      // For values outside that range, chain multiple atempo filters
      const atempoFilters = buildAtempoChain(speed);
      const filterStr = atempoFilters.map(v => `atempo=${v}`).join(',');

      const ffmpegArgs = [
        '-i', inputName,
        '-filter:a', filterStr,
      ];

      if (outputFormat === 'mp3') {
        ffmpegArgs.push('-codec:a', 'libmp3lame', '-q:a', '2');
      } else {
        ffmpegArgs.push('-codec:a', 'pcm_s16le');
      }

      ffmpegArgs.push(outputName);

      await ffmpeg.exec(ffmpegArgs);

      setProgressPct(92);
      setProgressLabel('Exporting…');

      const data = await ffmpeg.readFile(outputName);
      const mimeType = outputFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav';
      const blob = new Blob([data], { type: mimeType });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgressPct(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Processing failed. Please try a different file or format. Large files may exceed browser memory limits.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_${speed}x.${outputFormat}`;
    a.click();
  };

  const estimatedDuration = originalDuration != null ? originalDuration / speed : null;
  const speedLabel = speed === 1 ? '1× (Original)' : speed < 1 ? `${speed}× Slower` : `${speed}× Faster`;

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Zone */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">⏩</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">MP3, WAV, OGG, M4A, AAC, WebM, FLAC · Max 200 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* File info bar */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🎵</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)}</p>
            </div>
            <button
              onClick={handleReset}
              title="Reset"
              className="text-gray-400 hover:text-red-500 p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Duration info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Original Duration</p>
              <p className="font-bold text-gray-800 text-lg">{originalDuration != null ? formatDuration(originalDuration) : '—'}</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <p className="text-xs text-amber-600 mb-0.5">Estimated New Duration</p>
              <p className="font-bold text-amber-700 text-lg">{estimatedDuration != null ? formatDuration(estimatedDuration) : '—'}</p>
            </div>
          </div>

          {/* Original preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview at {speed}× speed</p>
            <audio
              ref={previewAudioRef}
              src={audioFile.url}
              controls
              className="w-full h-10"
              onLoadedMetadata={handleAudioLoaded}
            />
            <p className="text-xs text-gray-400 mt-1.5">* Browser preview uses playback rate. Export preserves pitch via ffmpeg.</p>
          </div>

          {/* Presets Panel */}
          <PresetsPanel
            presets={presets}
            onSave={(name) => savePreset(name, getCurrentSettings())}
            onLoad={applyPresetSettings}
            onDelete={deletePreset}
            accentColor="amber"
          />

          {/* Speed presets */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">Speed Presets</label>
              <span className="text-amber-600 font-bold text-sm bg-amber-50 px-2.5 py-0.5 rounded-lg">{speedLabel}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {SPEED_PRESETS.map(s => (
                <button
                  key={s}
                  onClick={() => { setSpeed(s); setResultUrl(null); }}
                  className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${speed === s ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-600'}`}
                >
                  {s}×
                </button>
              ))}
            </div>
          </div>

          {/* Custom slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">Custom Speed</label>
              <span className="text-xs text-gray-500">0.25× – 3×</span>
            </div>
            <input
              type="range"
              min={0.25}
              max={3}
              step={0.05}
              value={speed}
              onChange={(e) => { setSpeed(parseFloat(Number(e.target.value).toFixed(2))); setResultUrl(null); }}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0.25× (Slow)</span>
              <span>1× (Normal)</span>
              <span>3× (Fast)</span>
            </div>
          </div>

          {/* Output format */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Output Format</label>
            <div className="flex gap-2">
              {(['mp3', 'wav'] as const).map(fmt => (
                <button
                  key={fmt}
                  onClick={() => { setOutputFormat(fmt); setResultUrl(null); }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${outputFormat === fmt ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Large file warning */}
          {audioFile.size > 50 * 1024 * 1024 && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-100 rounded-xl p-3">
              <svg className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-yellow-700 text-xs">Large audio files may take longer because processing happens in your browser.</p>
            </div>
          )}

          {/* Progress bar */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">
                  Done! Speed changed to {speed}× · {formatBytes(resultSize)}
                </p>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {outputFormat.toUpperCase()}
              </button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            {!resultUrl && (
              <button
                onClick={handleProcess}
                disabled={processing}
                className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {processing ? 'Processing…' : `Apply ${speed}× Speed & Export`}
              </button>
            )}
            <button
              onClick={handleReset}
              disabled={processing}
              className="px-4 py-3.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-600 font-semibold rounded-xl transition-colors text-sm"
              title="Reset"
            >
              ↺ Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ffmpeg's atempo filter only accepts values in [0.5, 2.0].
 * For speeds outside that range, chain multiple atempo filters.
 */
function buildAtempoChain(speed: number): number[] {
  const filters: number[] = [];
  let remaining = speed;

  if (remaining < 0.5) {
    // Chain downward: each step can go as low as 0.5
    while (remaining < 0.5) {
      filters.push(0.5);
      remaining /= 0.5;
    }
    filters.push(remaining);
  } else if (remaining > 2.0) {
    // Chain upward: each step can go as high as 2.0
    while (remaining > 2.0) {
      filters.push(2.0);
      remaining /= 2.0;
    }
    filters.push(remaining);
  } else {
    filters.push(remaining);
  }

  return filters;
}
